import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Route, ArrowRight, Plane } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { ITIN_UI as UI } from "@/data/itinerary";
import { readItinerary, buildItinerary, tripStatus } from "@/lib/itinerary";
import { fill } from "@/lib/utils";

// Dashboard card: CTA when no plan yet; otherwise countdown / today's agenda / trip done.
export function ItineraryTodayCard() {
  const { tc } = useLang();
  const settings = useMemo(readItinerary, []);
  const days = useMemo(() => buildItinerary(settings), [settings]);
  const status = tripStatus(settings, days);

  if (!settings) {
    return (
      <Link to="/itinerary" data-testid="itinerary-cta-card">
        <Card className="rounded-3xl border-dashed border-primary/40 bg-primary/5 p-5 flex items-center gap-3 transition-colors hover:border-primary/70">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/10 text-primary"><Route className="h-6 w-6" /></span>
          <div className="min-w-0 flex-1">
            <p className="font-medium">{tc(UI.cardCta)}</p>
            <p className="text-xs text-muted-foreground">{tc(UI.cardHint)}</p>
          </div>
          <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rtl:-scale-x-100" />
        </Card>
      </Link>
    );
  }

  const day = status?.day;
  return (
    <Link to="/itinerary" data-testid="itinerary-today-card" data-phase={status?.phase}>
      <Card className="rounded-3xl border-primary/30 bg-primary/5 p-5 flex items-center gap-3 transition-colors hover:border-primary/60">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Plane className="h-6 w-6" /></span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-primary">{tc(UI.cardTitle)}</p>
          {status?.phase === "before" && <p className="font-medium" data-testid="itinerary-card-countdown">{fill(tc(UI.daysToGo), { n: status.daysToGo })}</p>}
          {status?.phase === "during" && day && (
            <>
              <p className="font-medium truncate" data-testid="itinerary-card-today">{fill(tc(UI.dayN), { n: day.n })} · {tc(day.title)}</p>
              <p className="text-xs text-muted-foreground truncate">{day.items.slice(0, 2).map((it) => tc(it.label)).join(" · ")}</p>
            </>
          )}
          {status?.phase === "after" && <p className="text-sm text-muted-foreground">{tc(UI.tripDone)}</p>}
          <p className="text-xs text-primary mt-0.5">{tc(UI.cardOpen)}</p>
        </div>
        <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0 rtl:-scale-x-100" />
      </Card>
    </Link>
  );
}
